import React, { useState } from 'react';
import { 
  FolderOpen, 
  Save, 
  FilePlus, 
  Trash2, 
  Edit3, 
  Download, 
  Check,
  X,
  Clock,
  FileText,
  ArrowLeftRight,
  Lock
} from 'lucide-react';
import { useWizard } from '../../context/WizardContext';
import { useSubscription } from '../../context/SubscriptionContext';
import ComparisonMode from './ComparisonMode';

const DesignManager = ({ isOpen, onClose }) => {
  const {
    savedDesigns,
    currentDesignId,
    currentDesignName,
    hasUnsavedChanges,
    saveCurrentDesign,
    saveAsNewDesign,
    loadDesign,
    createNewDesign,
    deleteDesign,
    renameDesign,
    exportDesign,
    wizardAComplete,
    wizardBComplete
  } = useWizard();
  
  const { hasFeature, triggerUpgradeModal } = useSubscription();
  const canCompare = hasFeature('compareMethodology');

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saveAsName, setSaveAsName] = useState('');
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [message, setMessage] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  if (!isOpen) return null;

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSave = () => {
    try {
      saveCurrentDesign();
      showMessage('Design saved successfully!');
    } catch (error) {
      showMessage('Failed to save design', 'error');
    }
  };

  const handleSaveAs = () => {
    if (!saveAsName.trim()) {
      showMessage('Please enter a name', 'error');
      return;
    }
    try {
      saveAsNewDesign(saveAsName.trim());
      setSaveAsName('');
      setShowSaveAs(false);
      showMessage('Design saved as new copy!');
    } catch (error) {
      showMessage('Failed to save design', 'error');
    }
  };

  const handleLoad = (id) => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Load anyway?')) {
        return;
      }
    }
    loadDesign(id);
    showMessage('Design loaded!');
    onClose();
  };

  const handleNew = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Create new design anyway?')) {
        return;
      }
    }
    createNewDesign();
    showMessage('New design created!');
    onClose();
  };

  const handleDelete = (id) => {
    deleteDesign(id);
    setConfirmDelete(null);
    showMessage('Design deleted');
  };

  const handleRename = (id) => {
    if (!editName.trim()) return;
    renameDesign(id, editName.trim());
    setEditingId(null);
    setEditName('');
    showMessage('Design renamed');
  };

  const handleExport = (id) => {
    try {
      exportDesign(id);
      showMessage('Design exported!');
    } catch (error) {
      showMessage('Failed to export design', 'error');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="design-manager-overlay" onClick={onClose}>
      <div className="design-manager-modal" onClick={e => e.stopPropagation()}>
        <div className="design-manager-header">
          <h2><FolderOpen size={24} /> Research Design Manager</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {message && (
          <div className={`design-manager-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="design-manager-current">
          <div className="current-design-info">
            <h3>Current Design</h3>
            <p className="current-name">
              {currentDesignName}
              {hasUnsavedChanges && <span className="unsaved-badge">Unsaved changes</span>}
            </p>
            <div className="current-status">
              <span className={`status-badge ${wizardAComplete ? 'complete' : ''}`}>
                Wizard A: {wizardAComplete ? 'Complete' : 'In Progress'}
              </span>
              <span className={`status-badge ${wizardBComplete ? 'complete' : ''}`}>
                Wizard B: {wizardBComplete ? 'Complete' : 'Not Started'}
              </span>
            </div>
          </div>
          
          <div className="current-design-actions">
            <button className="btn btn-primary" onClick={handleSave}>
              <Save size={18} /> Save
            </button>
            <button className="btn btn-secondary" onClick={() => setShowSaveAs(!showSaveAs)}>
              <FilePlus size={18} /> Save As...
            </button>
            <button className="btn btn-secondary" onClick={handleNew}>
              <FilePlus size={18} /> New Design
            </button>
          </div>

          {showSaveAs && (
            <div className="save-as-form">
              <div className="input-with-counter">
                <input
                  type="text"
                  placeholder="Enter new design name..."
                  value={saveAsName}
                  onChange={(e) => setSaveAsName(e.target.value.slice(0, 20))}
                  onKeyPress={(e) => e.key === 'Enter' && handleSaveAs()}
                  maxLength={20}
                />
                <span className="char-counter">{saveAsName.length}/20</span>
              </div>
              <button className="btn btn-primary btn-sm" onClick={handleSaveAs}>
                <Check size={16} /> Save
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowSaveAs(false)}>
                <X size={16} /> Cancel
              </button>
            </div>
          )}
        </div>

        <div className="design-manager-list">
          <div className="list-header">
            <h3>Saved Designs ({savedDesigns.length})</h3>
            <div className="list-actions">
              <button 
                className={`btn btn-secondary btn-sm ${!canCompare ? 'locked-feature-btn' : ''}`}
                onClick={() => canCompare ? setShowComparison(true) : triggerUpgradeModal('compareMethodology')}
                disabled={canCompare && savedDesigns.length < 2}
                title={!canCompare ? 'Upgrade to unlock Compare Methodology' : savedDesigns.length < 2 ? 'Need at least 2 designs to compare' : 'Compare designs side-by-side'}
              >
                {!canCompare && <Lock size={14} />}
                <ArrowLeftRight size={16} /> Compare Methodology
              </button>
            </div>
          </div>

          {savedDesigns.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <p>No saved designs yet</p>
              <p className="hint">Save your current design to see it here</p>
            </div>
          ) : (
            <div className="designs-list">
              {savedDesigns.map(design => (
                <div 
                  key={design.id} 
                  className={`design-item ${design.id === currentDesignId ? 'active' : ''}`}
                >
                  <div className="design-item-info">
                    {editingId === design.id ? (
                      <div className="rename-form">
                        <div className="input-with-counter">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value.slice(0, 20))}
                            onKeyPress={(e) => e.key === 'Enter' && handleRename(design.id)}
                            maxLength={20}
                            autoFocus
                          />
                          <span className="char-counter">{editName.length}/20</span>
                        </div>
                        <button className="btn-icon" onClick={() => handleRename(design.id)}>
                          <Check size={16} />
                        </button>
                        <button className="btn-icon" onClick={() => setEditingId(null)}>
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <h4>{design.name}</h4>
                        <div className="design-meta">
                          <span><Clock size={12} /> Updated: {formatDate(design.updatedAt)}</span>
                          <span className={`mini-badge ${design.wizardAComplete ? 'complete' : ''}`}>
                            A: {design.wizardAComplete ? '✓' : '○'}
                          </span>
                          <span className={`mini-badge ${design.wizardBComplete ? 'complete' : ''}`}>
                            B: {design.wizardBComplete ? '✓' : '○'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {confirmDelete === design.id ? (
                    <div className="confirm-delete">
                      <span>Delete?</span>
                      <button className="btn-icon danger" onClick={() => handleDelete(design.id)}>
                        <Check size={16} />
                      </button>
                      <button className="btn-icon" onClick={() => setConfirmDelete(null)}>
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="design-item-actions">
                      {design.id !== currentDesignId && (
                        <button 
                          className="btn btn-primary btn-sm"
                          onClick={() => handleLoad(design.id)}
                        >
                          Load
                        </button>
                      )}
                      <button 
                        className="btn-icon"
                        onClick={() => {
                          setEditingId(design.id);
                          setEditName(design.name);
                        }}
                        title="Rename"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button 
                        className="btn-icon"
                        onClick={() => handleExport(design.id)}
                        title="Export"
                      >
                        <Download size={16} />
                      </button>
                      <button 
                        className="btn-icon danger"
                        onClick={() => setConfirmDelete(design.id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ComparisonMode
        isOpen={showComparison}
        onClose={() => setShowComparison(false)}
      />
    </div>
  );
};

export default DesignManager;
